import { UseQueryResult } from '@tanstack/react-query'
import { Dispatch, SetStateAction, useEffect, useState } from 'react'

import { ShotlistsService } from '@/services/shotlist.service'
import { UploadFormService } from '@/services/uploadForm.service'

import { prefetchFormHeaderState } from '@/features/File/helpers/prefetchFormHeaderState'
import { refreshFormHeaderState } from '@/features/File/helpers/refreshFormHeaderState'
import { IShotlist } from '@/types/shotlist.interface'
import { ISelectedPlatform, TPlatformValue } from '@/types/uploadForm.interface'

interface UseFormUploaderBootstrapProps {
	taskId: string
	shotlist?: IShotlist | null
	openFormModal: boolean
	selectedPlatform: TPlatformValue | null
	selectedPlatformList: ISelectedPlatform[]
	setSelectedPlatform: (value: TPlatformValue | null) => void
	setSelectedPlatformList: Dispatch<SetStateAction<ISelectedPlatform[]>>
	refetchShotlist?: UseQueryResult<IShotlist, unknown>['refetch']
}

export const useFormUploaderBootstrap = ({
	taskId,
	shotlist,
	openFormModal,
	selectedPlatform,
	selectedPlatformList,
	setSelectedPlatform,
	setSelectedPlatformList,
	refetchShotlist
}: UseFormUploaderBootstrapProps) => {
	const [currentShotlist, setCurrentShotlist] = useState<IShotlist | null>(
		shotlist || null
	)
	const [serverHasVideo, setServerHasVideo] = useState(
		Boolean(shotlist?.has_video)
	)

	useEffect(() => {
		setCurrentShotlist(shotlist || null)
		setServerHasVideo(Boolean(shotlist?.has_video))
	}, [shotlist])

	useEffect(() => {
		if (!taskId || !openFormModal) return

		let cancelled = false

		const syncVideoState = async () => {
			try {
				const nextShotlist = await ShotlistsService.getShotlist(taskId)
				if (cancelled || !nextShotlist) return

				setCurrentShotlist(nextShotlist)
				setServerHasVideo(Boolean(nextShotlist.has_video))
			} catch (error) {
				console.log(error)
			}
		}

		syncVideoState()

		return () => {
			cancelled = true
		}
	}, [taskId, openFormModal])

	// This effect sets all the states for the FormHeaderStatus, when user every time comes to
	// the FormUploader from the Shotlist page
	useEffect(() => {
		if (!taskId) return
		if (selectedPlatformList?.length) return

		let cancelled = false

		const prefetch = async () => {
			try {
				const formData = await UploadFormService.getFormData(taskId)

				if (!formData || formData.is_completed !== false) return

				const nextShotlist = await ShotlistsService.getShotlist(taskId)

				if (!nextShotlist) return

				setCurrentShotlist(nextShotlist)
				setServerHasVideo(Boolean(nextShotlist.has_video))

				const { list } = prefetchFormHeaderState({
					formData,
					shotlist: nextShotlist
				})

				if (cancelled) return

				setSelectedPlatformList(list)

				if (!selectedPlatform) {
					setSelectedPlatform(list[0]?.value || null)
				}
			} catch (error) {
				console.log(error)
			}
		}

		prefetch()

		return () => {
			cancelled = true
		}
	}, [taskId])

	// This effect synchronize states when user deletes any translations on the Shotlist page and then returns to the Form
	useEffect(() => {
		if (!taskId) return

		let cancelled = false

		const sync = async () => {
			try {
				const formData = await UploadFormService.getFormData(taskId).catch(
					() => null
				)

				if (!formData || formData.is_completed !== false || cancelled) return

				const nextShotlist = await ShotlistsService.getShotlist(taskId)
				if (!nextShotlist || cancelled) return

				setCurrentShotlist(nextShotlist)
				setServerHasVideo(Boolean(nextShotlist.has_video))

				setSelectedPlatformList(prev => {
					if (!prev?.length) return prev
					const { next, changed } = refreshFormHeaderState({
						prev,
						shotlist: nextShotlist
					})
					return changed ? next : prev
				})
			} catch (error) {
				console.log(error)
			}
		}

		sync()

		return () => {
			cancelled = true
		}
	}, [taskId])

	const refreshCurrentShotlist = async () => {
		if (!taskId) return null

		if (refetchShotlist) {
			const result = await refetchShotlist()
			if (result.data) {
				setCurrentShotlist(result.data)
				setServerHasVideo(Boolean(result.data.has_video))
				return result.data
			}
		}

		const nextShotlist = await ShotlistsService.getShotlist(taskId)
		setCurrentShotlist(nextShotlist)
		setServerHasVideo(Boolean(nextShotlist?.has_video))
		return nextShotlist
	}

	return {
		currentShotlist,
		serverHasVideo,
		refreshCurrentShotlist
	}
}
